import { useState, useEffect } from 'react';

import { pushToDataLayer } from 'services/gtmService';
import { logBrazeSaveAdEvent } from 'services/brazeService';
import { SaveAd } from 'components/Toolkit/SaveAd/SaveAd';
import { accountsApi } from 'api/accountsApi';
import { fireToast } from 'helpers/Toasts';
import { useUserContext } from 'contexts/UserContext';
import StatusCode from 'status-code-enum';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';

interface ShowAndToggleSaveAdProps {
  adId: number;
  isViewingOwnAd?: boolean;
  userSaved?: boolean;
  isUnderlined?: boolean;
  isAdSaved?: boolean;
  className?: string;
  setIsAdSaved?: (t: boolean) => void;
  pulseOnLoad?: boolean;
  hasSavedAdAnimationOccurred?: boolean;
  setHasSavedAdAnimationOccurred?: (t: boolean) => void;
  isPreview?: boolean;
  subsection?: string;
  sellerType?: string;
}

function ShowAndToggleSaveAd({
  adId,
  isViewingOwnAd = false,
  isUnderlined = false,
  isAdSaved,
  setIsAdSaved,
  className,
  pulseOnLoad,
  hasSavedAdAnimationOccurred,
  setHasSavedAdAnimationOccurred,
  isPreview,
  subsection,
  sellerType,
}: ShowAndToggleSaveAdProps) {
  const { user, isLoginModalOpen } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const [queueSaveAd, setQueueSaveAd] = useState<boolean>(false);

  useEffect(() => {
    if (user?.id && queueSaveAd) {
      triggerToggleSave();
      setQueueSaveAd(false);
    }
    if (!isLoginModalOpen) {
      setQueueSaveAd(false);
    }
  }, [isLoginModalOpen]);

  useEffect(() => {
    if (!user?.id) {
      setIsAdSaved && setIsAdSaved(false);
    }
  }, [user?.id]);

  function handleUserEvent() {
    user?.id ? triggerToggleSave() : loginAndSave();
  }

  function loginAndSave() {
    setQueueSaveAd(true);
    handleLogin();
  }

  async function triggerToggleSave() {
    const res = isAdSaved !== undefined && (await handleRequest(isAdSaved));
    if (res) {
      setIsAdSaved && setIsAdSaved(!isAdSaved);
    }
    if (!isAdSaved) {
      pushToDataLayer({
        event: 'create_saved_ad',
      });
      logBrazeSaveAdEvent(subsection, sellerType);
    } else {
      pushToDataLayer({
        event: 'delete_saved_ad',
      });
    }
  }

  async function handleRequest(savedStatus: boolean) {
    try {
      const { status } = savedStatus
        ? await accountsApi.unsaveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          )
        : await accountsApi.saveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          );
      return status === StatusCode.SuccessOK;
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  }

  return !isViewingOwnAd ? (
    <SaveAd
      toggleSave={!isPreview ? handleUserEvent : undefined}
      isAdSaved={isAdSaved}
      isUnderlined={isUnderlined}
      pulseOnLoad={pulseOnLoad}
      hasSavedAdAnimationOccurred={hasSavedAdAnimationOccurred}
      setHasSavedAdAnimationOccurred={setHasSavedAdAnimationOccurred}
      className={className}
    />
  ) : null;
}

export { ShowAndToggleSaveAd };
